import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  FaDownload,
  FaPen,
  FaPlus,
  FaSearch,
  FaTag,
  FaTimes,
  FaTrash,
  FaUserGraduate,
  FaUsers
} from 'react-icons/fa';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  LabelChip,
  LabelPicker,
  Pagination,
  SortHeader
} from '../shared/ui';
import Modal from '../components/Modal';
import ManageLabelsModal from '../components/ManageLabelsModal';
import FilterSidebar from './directory/FilterSidebar';
import FamilyEditorModal from '../components/directory/FamilyEditorModal';
import StudentEditorModal from '../components/directory/StudentEditorModal';
import { PopupContext } from '../App';
import { API_URL } from '../config/env';
import {
  createLabel as apiCreateLabel,
  setFamilyLabels,
  setStudentLabels
} from '../services/labels';
import styles from './DirectoryPage.module.css';
import {
  PAGE_SIZE_DEFAULT,
  PAGE_SIZE_MAX,
  PAGE_SIZE_MIN,
  setTablePageSizePreference,
  useTablePageSizePreference
} from '../shared/ui/tablePageSize';

const ROW_MEASURE_DEBOUNCE_MS = 120;
/** Fallback heights before tbody paints; keep in sync with .table row density in DirectoryPage.module.css */
const FALLBACK_THEAD_H = 32;
const FALLBACK_ROW_H = 34;
const SEARCH_DEBOUNCE_MS = 300;

function computeFitRowsForContainer(containerEl) {
  if (!containerEl || containerEl.clientHeight < FALLBACK_THEAD_H + FALLBACK_ROW_H) return null;
  const table = containerEl.querySelector('table');
  const thead = table?.querySelector('thead');
  const row = table?.querySelector('tbody tr');
  const theadH = thead?.offsetHeight ?? FALLBACK_THEAD_H;
  const rowRect = row?.getBoundingClientRect();
  const rowH = rowRect && rowRect.height >= 12 ? rowRect.height : FALLBACK_ROW_H;
  const avail = containerEl.clientHeight - theadH - 8;
  if (avail <= 0) return null;
  return Math.max(PAGE_SIZE_MIN, Math.min(PAGE_SIZE_MAX, Math.floor(avail / rowH)));
}
const TABS = [
  { id: 'students', label: 'Students', icon: FaUserGraduate, path: '/students' },
  { id: 'families', label: 'Families', icon: FaUsers, path: '/families' }
];

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    ...extra
  };
}

function formatAddress(fam) {
  if (!fam) return '—';
  const line = [fam.address_line1, fam.address_line2].filter(Boolean).join(' ');
  const cityLine = [fam.city, fam.state].filter(Boolean).join(', ');
  const zip = fam.zip_code ? ` ${fam.zip_code}` : '';
  const result = [line, `${cityLine}${zip}`.trim()].filter(Boolean).join(' • ');
  return result || '—';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function parseLabelIdsFromUrl(value) {
  if (!value) return [];
  return value
    .split(',')
    .map((v) => parseInt(v.trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
}

/** Total row count from paginated families/students API payload */
function extractListTotal(payload) {
  if (payload == null) return null;
  if (Array.isArray(payload)) return payload.length;
  const t = payload.total;
  if (typeof t === 'number' && !Number.isNaN(t)) return t;
  if (typeof t === 'string') {
    const n = parseInt(t, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

export default function DirectoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const showPopup = useContext(PopupContext);

  const tab = useMemo(() => {
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (tabParam === 'students' || tabParam === 'families') return tabParam;
    return location.pathname === '/families' ? 'families' : 'students';
  }, [params, location.pathname]);

  const urlSearch = params.get('search') || '';
  const rawStatus = (params.get('status') || '').toLowerCase();
  const legacyInactive = params.get('inactive') === '1' || params.get('inactive') === 'true';
  const status = ['active', 'inactive', 'all'].includes(rawStatus)
    ? rawStatus
    : legacyInactive
      ? 'inactive'
      : 'active';
  const pageSizePref = useTablePageSizePreference();
  const urlPageSize = Math.min(
    PAGE_SIZE_MAX,
    Math.max(PAGE_SIZE_MIN, parseInt(params.get('size') || String(PAGE_SIZE_DEFAULT), 10) || PAGE_SIZE_DEFAULT)
  );
  const pageSize = pageSizePref.mode === 'manual' ? pageSizePref.size : urlPageSize;
  const page = Math.max(1, parseInt(params.get('page') || '1', 10));
  const sort = params.get('sort') || 'last_name';
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const selectedLabelIds = useMemo(
    () => parseLabelIdsFromUrl(params.get('labels') || ''),
    [params]
  );

  const totalsQueryString = useMemo(() => {
    const q = new URLSearchParams();
    q.set('page', '1');
    q.set('size', '1');
    if (urlSearch) q.set('search', urlSearch);
    q.set('status', status);
    q.set('sort', 'last_name');
    q.set('dir', 'asc');
    if (selectedLabelIds.length > 0) {
      q.set('label_ids', selectedLabelIds.join(','));
    }
    return q.toString();
  }, [urlSearch, status, selectedLabelIds]);

  const [searchInput, setSearchInput] = useState(urlSearch);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setSearchInput(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    if (debouncedSearch === urlSearch) return;
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set('search', debouncedSearch);
    else next.delete('search');
    next.delete('page');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const rowsRef = useRef([]);
  rowsRef.current = rows;
  const [labels, setLabels] = useState([]);
  const [selection, setSelection] = useState(new Set());

  const [familyModal, setFamilyModal] = useState({ open: false, mode: 'create', record: null });
  const [studentModal, setStudentModal] = useState({ open: false, mode: 'create', record: null });
  const [pendingStudentFamilyId, setPendingStudentFamilyId] = useState(null);

  const [confirm, setConfirm] = useState(null);
  const [labelManagerOpen, setLabelManagerOpen] = useState(false);
  const [familiesLookup, setFamiliesLookup] = useState([]);
  const familyModalFromStudentRef = useRef(false);
  const [overview, setOverview] = useState(null);
  const [tabTotals, setTabTotals] = useState({ families: null, students: null });
  const [tabTotalsVersion, setTabTotalsVersion] = useState(0);
  const bumpTabTotals = useCallback(() => setTabTotalsVersion((n) => n + 1), []);
  const tableBodyRef = useRef(null);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const studentAndFamilyStacked = studentModal.open && familyModal.open;

  const flash = useCallback(
    (message, type = 'success') => {
      showPopup(message, type);
    },
    [showPopup]
  );

  const setUrl = useCallback((updates, { resetPage = true } = {}) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '' || v === false) {
            next.delete(k);
          } else {
            next.set(k, String(v));
          }
        });
        if (resetPage && !('page' in updates)) next.delete('page');
        return next;
      },
      { replace: true }
    );
  }, [setParams]);

  const setTab = useCallback(
    (next) => {
      if (next === tab) return;
      const target = TABS.find((t) => t.id === next);
      const nextParams = new URLSearchParams(params);
      nextParams.delete('page');
      nextParams.delete('sort');
      nextParams.delete('dir');
      nextParams.delete('labels');
      nextParams.delete('tab');
      navigate({ pathname: target.path, search: nextParams.toString() });
    },
    [tab, params, navigate]
  );

  const fetchLabels = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/labels?include_counts=1`, {
        headers: authHeaders()
      });
      if (!res.ok) return;
      const list = await res.json();
      if (!Array.isArray(list)) return;
      setLabels(
        list.map((l) => ({
          ...l,
          family_count: Number(l.family_count ?? 0) || 0,
          student_count: Number(l.student_count ?? 0) || 0
        }))
      );
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchLabels();
  }, [fetchLabels]);

  const refreshFamiliesLookup = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/families?size=500&status=all&sort=last_name`, {
        headers: authHeaders()
      });
      const d = await r.json();
      setFamiliesLookup(Array.isArray(d) ? d : d?.data || []);
    } catch {
      setFamiliesLookup([]);
    }
  }, []);

  useEffect(() => {
    refreshFamiliesLookup();
  }, [refreshFamiliesLookup]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/directory/overview`, { headers: authHeaders() })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setOverview(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchRequest = useRef(0);
  /** Tab that `rows`/`total` last successfully matched (avoid skeleton flash on filter/page refresh). */
  const lastFetchedTabRef = useRef(null);

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    q.set('page', String(page));
    q.set('size', String(pageSize));
    if (urlSearch) q.set('search', urlSearch);
    q.set('status', status);
    q.set('sort', sort);
    q.set('dir', dir);
    if (selectedLabelIds.length > 0) {
      q.set('label_ids', selectedLabelIds.join(','));
    }
    return q.toString();
  }, [page, pageSize, urlSearch, status, sort, dir, selectedLabelIds]);

  const fetchRows = useCallback(() => {
    const requestId = ++fetchRequest.current;
    setLoading(true);
    const endpoint = tab === 'families' ? 'families' : 'students';
    fetch(`${API_URL}/api/${endpoint}?${buildQuery()}`, { headers: authHeaders() })
      .then(async (r) => {
        if (!r.ok) throw new Error('Failed to load');
        return r.json();
      })
      .then((data) => {
        if (requestId !== fetchRequest.current) return;
        const list = Array.isArray(data) ? data : data?.data || [];
        const totalCount = Array.isArray(data) ? data.length : data?.total ?? list.length;
        setRows(list);
        setTotal(totalCount);
        lastFetchedTabRef.current = tab;
      })
      .catch(() => {
        if (requestId !== fetchRequest.current) return;
        const preserveStale =
          rowsRef.current.length > 0 && lastFetchedTabRef.current === tab;
        if (!preserveStale) {
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (requestId !== fetchRequest.current) return;
        setLoading(false);
      });
  }, [tab, buildQuery]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    let cancelled = false;
    const qs = totalsQueryString;
    Promise.all([
      fetch(`${API_URL}/api/families?${qs}`, { headers: authHeaders() }).then((r) =>
        r.ok ? r.json() : null
      ),
      fetch(`${API_URL}/api/students?${qs}`, { headers: authHeaders() }).then((r) =>
        r.ok ? r.json() : null
      )
    ])
      .then(([famPayload, studPayload]) => {
        if (cancelled) return;
        setTabTotals({
          families: extractListTotal(famPayload),
          students: extractListTotal(studPayload)
        });
      })
      .catch(() => {
        if (!cancelled) setTabTotals({ families: null, students: null });
      });
    return () => {
      cancelled = true;
    };
  }, [totalsQueryString, tabTotalsVersion]);

  useEffect(() => {
    if (pageSizePref.mode !== 'manual') return;
    if (urlPageSize === pageSizePref.size) return;
    setUrl({ size: pageSizePref.size }, { resetPage: false });
  }, [pageSizePref, urlPageSize, setUrl]);

  const handlePageSizeChange = useCallback(
    (next) => {
      if (next === 'auto') {
        setTablePageSizePreference({ mode: 'auto' });
        return;
      }
      const size = Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, Number(next) || PAGE_SIZE_DEFAULT));
      setTablePageSizePreference({ mode: 'manual', size });
      setUrl({ size, page: 1 }, { resetPage: false });
    },
    [setUrl]
  );

  useEffect(() => {
    if (pageSizePref.mode === 'manual') return undefined;
    const el = tableBodyRef.current;
    if (!el) return undefined;

    let timer;
    const apply = () => {
      if (loading) return;
      const next = computeFitRowsForContainer(el);
      if (next == null) return;
      const cur = Math.min(
        PAGE_SIZE_MAX,
        Math.max(
          PAGE_SIZE_MIN,
          parseInt(paramsRef.current.get('size') || String(PAGE_SIZE_DEFAULT), 10)
        )
      );
      if (next === cur) return;
      /* Only adjust fitted row count; do not reset page (layout remeasures often fight pagination). */
      setUrl({ size: next }, { resetPage: false });
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(apply, ROW_MEASURE_DEBOUNCE_MS);
    };

    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    schedule();

    return () => {
      window.clearTimeout(timer);
      ro.disconnect();
    };
  }, [setUrl, tab, loading, pageSizePref.mode]);

  useEffect(() => {
    if (total <= 0) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page <= maxPage) return;
    setUrl({ page: maxPage }, { resetPage: false });
  }, [total, pageSize, page, setUrl]);

  // Clear selection when query criteria change
  useEffect(() => {
    setSelection(new Set());
  }, [tab, page, pageSize, urlSearch, status, sort, dir, selectedLabelIds]);

  /** Export all rows matching current filters (paginates; API max page size is 300). */
  const exportCurrent = async () => {
    try {
      const endpoint = tab === 'families' ? 'families' : 'students';
      const batchSize = 200;
      const list = [];
      let pageNum = 1;
      let totalExpected = null;

      while (totalExpected === null || list.length < totalExpected) {
        const q = new URLSearchParams();
        q.set('page', String(pageNum));
        q.set('size', String(batchSize));
        if (urlSearch) q.set('search', urlSearch);
        q.set('status', status);
        q.set('sort', sort);
        q.set('dir', dir);
        if (selectedLabelIds.length > 0) q.set('label_ids', selectedLabelIds.join(','));
        const res = await fetch(`${API_URL}/api/${endpoint}?${q.toString()}`, {
          headers: authHeaders()
        });
        if (!res.ok) throw new Error('Export fetch failed');
        const data = await res.json();
        const chunk = Array.isArray(data) ? data : data?.data || [];
        if (totalExpected === null) {
          totalExpected = Array.isArray(data) ? chunk.length : data?.total ?? chunk.length;
        }
        list.push(...chunk);
        if (chunk.length === 0) break;
        if (chunk.length < batchSize || list.length >= totalExpected) break;
        pageNum += 1;
        if (pageNum > 500) break;
      }
      if (tab === 'families') {
        const sheet = list.map((f) => ({
          'Case #': f.case_number,
          'First Name': f.first_name,
          'Last Name': f.last_name,
          Email: f.email,
          Phone: f.phone_number,
          Address: [f.address_line1, f.address_line2].filter(Boolean).join(' '),
          City: f.city,
          State: f.state,
          'ZIP Code': f.zip_code,
          Active: f.is_active ? 'Yes' : 'No',
          Students: f.student_count || 0,
          Labels: (f.labels || []).map((l) => l.name).join(', ')
        }));
        const ws = XLSX.utils.json_to_sheet(sheet);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Families');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'families_export.xlsx');
      } else {
        const sheet = list.map((s) => ({
          CIN: s.student_id,
          'First Name': s.first_name,
          'Last Name': s.last_name || (s.family?.last_name ?? ''),
          DOB: s.dob,
          'Family Case #': s.family?.case_number || '',
          Family: s.family ? `${s.family.first_name || ''} ${s.family.last_name || ''}`.trim() : '',
          Expected: s.expected ?? '',
          Received: s.received ?? '',
          Active: s.is_active ? 'Yes' : 'No',
          Labels: (s.labels || []).map((l) => l.name).join(', ')
        }));
        const ws = XLSX.utils.json_to_sheet(sheet);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Students');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'students_export.xlsx');
      }
    } catch (e) {
      flash('Export failed', 'error');
    }
  };

  const openCreateFamily = () => {
    familyModalFromStudentRef.current = false;
    setFamilyModal({ open: true, mode: 'create', record: null });
  };
  const openCreateFamilyFromStudent = useCallback(() => {
    familyModalFromStudentRef.current = true;
    setFamilyModal({ open: true, mode: 'create', record: null });
  }, []);
  const openEditFamily = (fam) => {
    familyModalFromStudentRef.current = false;
    setFamilyModal({ open: true, mode: 'edit', record: fam });
  };
  const closeFamilyModal = () => {
    familyModalFromStudentRef.current = false;
    setFamilyModal({ open: false, mode: 'create', record: null });
  };

  const openCreateStudent = () => {
    setPendingStudentFamilyId(null);
    setStudentModal({ open: true, mode: 'create', record: null });
  };
  const openEditStudent = (s) => {
    setPendingStudentFamilyId(null);
    setStudentModal({ open: true, mode: 'edit', record: s });
  };
  const closeStudentModal = () => {
    setPendingStudentFamilyId(null);
    setStudentModal({ open: false, mode: 'create', record: null });
  };

  const handleDelete = async () => {
    if (!confirm) return;
    const { kind, record } = confirm;
    const url =
      kind === 'family'
        ? `${API_URL}/api/families/${record.id}`
        : `${API_URL}/api/students/${record.id}`;
    try {
      const res = await fetch(url, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Deactivate failed');
      flash(kind === 'family' ? 'Family deactivated' : 'Student deactivated');
      setConfirm(null);
      fetchRows();
      fetchLabels();
      bumpTabTotals();
    } catch (err) {
      flash(err.message || 'Deactivate failed', 'error');
    }
  };

  const handleSort = ({ sort: nextSort, dir: nextDir }) => {
    setUrl({ sort: nextSort, dir: nextDir });
  };

  const onCreateLabelInline = async (name) => {
    try {
      const created = await apiCreateLabel({ name, color: 'neutral', kind: 'both' });
      setLabels((list) =>
        [...list, { ...created, family_count: 0, student_count: 0 }].sort((a, b) =>
          a.name.localeCompare(b.name)
        )
      );
      return created;
    } catch (e) {
      flash(e.message, 'error');
      return null;
    }
  };

  const commitRowLabels = useCallback(
    async (entity, labelIds) => {
      try {
        const data =
          tab === 'families'
            ? await setFamilyLabels(entity.id, labelIds)
            : await setStudentLabels(entity.id, labelIds);
        const nextLabels = data.labels ?? [];
        setRows((rs) =>
          rs.map((r) => (r.id === entity.id ? { ...r, labels: nextLabels } : r))
        );
        fetchLabels();
      } catch (err) {
        flash(err.message || 'Failed to update labels', 'error');
        fetchRows();
      }
    },
    [tab, flash, fetchLabels, fetchRows]
  );

  // Selection helpers
  const toggleRow = (id) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelection((prev) => {
      if (prev.size === rows.length && rows.length > 0) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const handleBulkSetStatus = async (active) => {
    if (selection.size === 0) return;
    const endpoint = tab === 'families' ? 'families' : 'students';
    try {
      const res = await fetch(`${API_URL}/api/${endpoint}/bulk/status`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ids: [...selection], is_active: active })
      });
      if (!res.ok) throw new Error('Bulk update failed');
      flash(`${selection.size} updated`);
      setSelection(new Set());
      fetchRows();
      bumpTabTotals();
    } catch (e) {
      flash(e.message, 'error');
    }
  };

  // Filter labels by tab type
  const sidebarLabels = useMemo(() => {
    if (tab === 'families') {
      return labels.filter((l) => l.kind === 'family' || l.kind === 'both');
    }
    return labels.filter((l) => l.kind === 'student' || l.kind === 'both');
  }, [labels, tab]);

  const showTableSkeleton =
    loading &&
    (rows.length === 0 || tab !== lastFetchedTabRef.current);

  const setStatus = (next) => {
    setUrl({ status: next, inactive: '' });
  };

  const toggleLabelFilter = (id) => {
    const next = new Set(selectedLabelIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setUrl({ labels: [...next].join(',') });
  };

  return (
    <>
      <div className={styles.pageRoot}>
      <div className={styles.tabsBar}>
        <div className={styles.tabsInner}>
          <div className={styles.tabsRow} role="tablist" aria-label="Directory tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              const badgeTotal =
                t.id === 'families' ? tabTotals.families : tabTotals.students;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={[styles.tabBtn, active ? styles.tabBtnActive : ''].join(' ')}
                  onClick={() => setTab(t.id)}
                >
                  <Icon className={styles.tabIcon} />
                  <span>{t.label}</span>
                  <span
                    className={styles.tabCount}
                    aria-label={
                      badgeTotal != null
                        ? `${badgeTotal.toLocaleString()} ${t.label}`
                        : `${t.label} count loading`
                    }
                  >
                    {badgeTotal != null ? badgeTotal.toLocaleString() : '…'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        <FilterSidebar
          status={status}
          onStatusChange={setStatus}
          labels={sidebarLabels}
          selectedLabelIds={selectedLabelIds}
          onToggleLabel={toggleLabelFilter}
          onClearLabels={() => setUrl({ labels: '' })}
          onSelectAllLabels={() =>
            setUrl({ labels: sidebarLabels.map((l) => l.id).join(',') })
          }
          onManageLabels={() => setLabelManagerOpen(true)}
        />

        <div className={styles.main}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarMain}>
              <div className={styles.search}>
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder={
                    tab === 'families'
                      ? 'Search families'
                      : 'Search students'
                  }
                  iconLeft={<FaSearch />}
                />
              </div>
              <div className={styles.toolbarActions}>
                <Button variant="ghost" iconLeft={<FaTag />} onClick={() => setLabelManagerOpen(true)}>
                  Manage labels
                </Button>
                <Button variant="secondary" iconLeft={<FaDownload />} onClick={exportCurrent}>
                  Export
                </Button>
                {tab === 'families' ? (
                  <Button iconLeft={<FaPlus />} onClick={openCreateFamily}>
                    New family
                  </Button>
                ) : (
                  <Button iconLeft={<FaPlus />} onClick={openCreateStudent}>
                    Add student
                  </Button>
                )}
              </div>
            </div>
            {selectedLabelIds.length > 0 ? (
              <div className={styles.filterPills}>
                <span className={styles.filterPillsLabel}>Filtered by labels:</span>
                {sidebarLabels
                  .filter((l) => selectedLabelIds.includes(l.id))
                  .map((l) => (
                    <LabelChip
                      key={l.id}
                      label={l.name}
                      color={l.color}
                      onRemove={() => toggleLabelFilter(l.id)}
                    />
                  ))}
                <button
                  type="button"
                  className={styles.clearFilters}
                  onClick={() => setUrl({ labels: '' })}
                >
                  Clear
                </button>
              </div>
            ) : null}
          </div>

          {overview ? (
            <div className={styles.overviewStrip} aria-label="Overview statistics">
              <div className={styles.overviewCard}>
                <div className={styles.overviewLabel}>Active students</div>
                <div className={styles.overviewValue}>
                  {(overview.activeStudents ?? 0).toLocaleString()}
                </div>
              </div>
              <div className={styles.overviewCard}>
                <div className={styles.overviewLabel}>Active families</div>
                <div className={styles.overviewValue}>
                  {(overview.activeFamilies ?? 0).toLocaleString()}
                </div>
              </div>
              <div className={`${styles.overviewCard} ${styles.overviewCardWide}`}>
                <div className={styles.overviewLabel}>Latest voucher period received</div>
                <div className={styles.overviewValue}>
                  {overview.latestSettlementPeriodReceived != null
                    ? formatMoney(overview.latestSettlementPeriodReceived)
                    : '—'}
                </div>
                <div className={styles.overviewSub}>
                  {overview.latestSettlementDateRange
                    ? overview.latestSettlementDateRange
                    : overview.latestSettlementDate
                      ? formatDate(overview.latestSettlementDate)
                      : 'No settled vouchers yet'}
                </div>
              </div>
              <div className={styles.overviewCard}>
                <div className={styles.overviewLabel}>Total received (all periods)</div>
                <div className={styles.overviewValue}>
                  {formatMoney(overview.totalReceivedAllTime ?? 0)}
                </div>
              </div>
            </div>
          ) : null}

          <Card className={styles.tableCard}>
            <CardBody ref={tableBodyRef} className={styles.tableBody}>
              {showTableSkeleton ? (
                <div className={styles.skeleton}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={styles.skeletonRow}
                      style={{ animationDelay: `${i * 80}ms` }}
                    />
                  ))}
                </div>
              ) : rows.length === 0 ? (
                <Empty
                  title={tab === 'families' ? 'No families found.' : 'No students found.'}
                  description="Try adjusting your search or filters."
                />
              ) : tab === 'families' ? (
                <FamiliesTable
                  rows={rows}
                  sort={sort}
                  dir={dir}
                  onSort={handleSort}
                  onViewDetail={(f) => navigate(`/families/${f.id}`)}
                  onEdit={openEditFamily}
                  onDelete={(record) => setConfirm({ kind: 'family', record })}
                  labelsAvailable={sidebarLabels}
                  onCommitRowLabels={commitRowLabels}
                  onCreateLabelRow={onCreateLabelInline}
                  selection={selection}
                  onToggleRow={toggleRow}
                  onToggleAll={toggleAll}
                  allSelected={selection.size === rows.length && rows.length > 0}
                />
              ) : (
                <StudentsTable
                  rows={rows}
                  sort={sort}
                  dir={dir}
                  onSort={handleSort}
                  onViewDetail={(s) => navigate(`/students/${s.id}`)}
                  onEdit={openEditStudent}
                  onDelete={(record) => setConfirm({ kind: 'student', record })}
                  labelsAvailable={sidebarLabels}
                  onCommitRowLabels={commitRowLabels}
                  onCreateLabelRow={onCreateLabelInline}
                  selection={selection}
                  onToggleRow={toggleRow}
                  onToggleAll={toggleAll}
                  allSelected={selection.size === rows.length && rows.length > 0}
                />
              )}
            </CardBody>
          </Card>

          <div className={styles.footRow}>
            <div className={styles.pagerFoot}>
              {total > 0 ? (
                <div className={styles.pagerCenter}>
                  <Pagination
                    page={page}
                    pageSize={pageSize}
                    sizeMode={pageSizePref.mode}
                    total={total}
                    onPageChange={(n) => setUrl({ page: n }, { resetPage: false })}
                    onPageSizeChange={handlePageSizeChange}
                  />
                </div>
              ) : null}
              <div className={styles.inactiveListHint}>
                {status === 'inactive' ? (
                  <>
                    <span>Showing inactive records only.</span>
                    <button
                      type="button"
                      className={styles.inactiveLink}
                      onClick={() => setUrl({ status: 'active', inactive: '' })}
                    >
                      Show active records
                    </button>
                  </>
                ) : status === 'all' ? (
                  <span>Status filter: all (active and inactive). Use the sidebar to switch.</span>
                ) : (
                  <button
                    type="button"
                    className={styles.inactiveLink}
                    onClick={() => setUrl({ status: 'inactive', inactive: '' })}
                  >
                    Show inactive records
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selection.size > 0 ? (
        <div className={styles.bulkBar} role="region" aria-label="Bulk actions">
          <div className={styles.bulkBarInner}>
            <span className={styles.bulkCount}>{selection.size} selected</span>
            <button
              type="button"
              className={styles.bulkBtn}
              onClick={() => handleBulkSetStatus(false)}
            >
              Mark as inactive
            </button>
            <button
              type="button"
              className={styles.bulkBtn}
              onClick={() => handleBulkSetStatus(true)}
            >
              Mark as active
            </button>
            <button
              type="button"
              className={styles.bulkClose}
              onClick={() => setSelection(new Set())}
              aria-label="Clear selection"
            >
              <FaTimes />
            </button>
          </div>
        </div>
      ) : null}

      </div>

      {/* Family create/edit modal */}
      <FamilyEditorModal
        open={familyModal.open}
        onClose={closeFamilyModal}
        mode={familyModal.mode}
        record={familyModal.record}
        labels={labels}
        onLabelsRefresh={fetchLabels}
        onSaved={(saved, meta) => {
          const fromStudent = familyModalFromStudentRef.current;
          void refreshFamiliesLookup();
          if (meta.mode === 'create' && fromStudent) {
            setPendingStudentFamilyId(saved.id);
          }
          fetchRows();
          bumpTabTotals();
        }}
        flash={flash}
        zIndex={studentAndFamilyStacked ? 2100 : 2000}
      />

      <StudentEditorModal
        open={studentModal.open}
        onClose={closeStudentModal}
        mode={studentModal.mode}
        record={studentModal.record}
        labels={labels}
        familiesLookup={familiesLookup}
        onLabelsRefresh={fetchLabels}
        onSaved={() => {
          fetchRows();
          bumpTabTotals();
        }}
        onAddNewFamily={openCreateFamilyFromStudent}
        flash={flash}
        zIndex={studentAndFamilyStacked ? 1990 : 2000}
        familyModalOpen={familyModal.open}
        selectFamilyId={pendingStudentFamilyId}
      />

      {/* Confirm deactivate */}
      <Modal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm?.kind === 'family' ? 'Deactivate this family?' : 'Deactivate this student?'}
        description={
          confirm?.kind === 'family'
            ? 'The household will be marked inactive and all students in this family will be set inactive. You can reactivate from the profile or inactive list.'
            : 'The student will be marked inactive. You can reactivate from their profile or the inactive list.'
        }
        size="sm"
      >
        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button type="button" variant="danger" onClick={handleDelete}>
            Deactivate
          </Button>
        </div>
      </Modal>

      <ManageLabelsModal
        open={labelManagerOpen}
        onClose={() => setLabelManagerOpen(false)}
        onChanged={() => {
          fetchLabels();
          fetchRows();
        }}
      />
    </>
  );
}

function Empty({ title, description }) {
  return (
    <div className={styles.empty}>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}

function RowLabelPicker({ entity, available, onCommit, onCreateLabel }) {
  const selectedIds = useMemo(() => (entity.labels || []).map((l) => l.id), [entity.labels]);
  return (
    <div
      className={styles.rowLabelPicker}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <LabelPicker
        compact
        available={available}
        selectedIds={selectedIds}
        onChange={(ids) => {
          void onCommit(entity, ids);
        }}
        onCreateLabel={onCreateLabel}
        placeholder="Add label"
      />
    </div>
  );
}

function FamiliesTable({
  rows,
  sort,
  dir,
  onSort,
  onViewDetail,
  onEdit,
  onDelete,
  labelsAvailable,
  onCommitRowLabels,
  onCreateLabelRow,
  selection,
  onToggleRow,
  onToggleAll,
  allSelected
}) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.checkCell}>
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allSelected}
                onChange={onToggleAll}
              />
            </th>
            <th>
              <SortHeader
                column="last_name"
                label="Family"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="case_number"
                label="Case #"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>Phone</th>
            <th>
              <SortHeader
                column="city"
                label="Address"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="labels"
                label="Labels"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="student_count"
                label="Students"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
                align="center"
              />
            </th>
            <th>
              <SortHeader
                column="is_active"
                label="Status"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.id}
              className={[
                f.is_active ? '' : styles.rowInactive,
                selection.has(f.id) ? styles.rowSelected : ''
              ].join(' ')}
            >
              <td className={styles.checkCell}>
                <input
                  type="checkbox"
                  aria-label={`Select ${f.first_name} ${f.last_name}`}
                  checked={selection.has(f.id)}
                  onChange={() => onToggleRow(f.id)}
                />
              </td>
              <td>
                <button
                  type="button"
                  className={styles.entityBtn}
                  onClick={() => onViewDetail(f)}
                >
                  <span className={styles.entityName}>
                    {f.first_name} {f.last_name}
                  </span>
                  <span className={styles.entityMeta}>{f.email || '—'}</span>
                </button>
              </td>
              <td className={styles.compactBadgeCell}>
                <Badge variant="neutral">#{f.case_number}</Badge>
              </td>
              <td>
                <span className={styles.subtle}>{f.phone_number || '—'}</span>
              </td>
              <td>
                <span className={styles.addr}>{formatAddress(f)}</span>
              </td>
              <td>
                <RowLabelPicker
                  entity={f}
                  available={labelsAvailable}
                  onCommit={onCommitRowLabels}
                  onCreateLabel={onCreateLabelRow}
                />
              </td>
              <td className={styles.centerCell}>
                <span className={styles.countPill}>{f.student_count || 0}</span>
              </td>
              <td className={styles.compactBadgeCell}>
                <Badge variant={f.is_active ? 'success' : 'neutral'}>
                  {f.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className={styles.actionsCell}>
                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={styles.linkAction}
                    onClick={() => onEdit(f)}
                  >
                    <FaPen aria-hidden="true" /> Edit
                  </button>
                  <button
                    type="button"
                    className={[styles.linkAction, styles.danger].join(' ')}
                    onClick={() => onDelete(f)}
                    aria-label="Deactivate family"
                  >
                    <FaTrash aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StudentsTable({
  rows,
  sort,
  dir,
  onSort,
  onViewDetail,
  onEdit,
  onDelete,
  labelsAvailable,
  onCommitRowLabels,
  onCreateLabelRow,
  selection,
  onToggleRow,
  onToggleAll,
  allSelected
}) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.checkCell}>
              <input
                type="checkbox"
                aria-label="Select all"
                checked={allSelected}
                onChange={onToggleAll}
              />
            </th>
            <th>
              <SortHeader
                column="last_name"
                label="Student"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="student_id"
                label="CIN"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="dob"
                label="DOB"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="family_name"
                label="Family"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="labels"
                label="Labels"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th>
              <SortHeader
                column="expected"
                label="Expected"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
                align="right"
              />
            </th>
            <th>
              <SortHeader
                column="received"
                label="Received"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
                align="right"
              />
            </th>
            <th>
              <SortHeader
                column="is_active"
                label="Status"
                currentSort={sort}
                currentDir={dir}
                onSort={onSort}
              />
            </th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const fam = s.family;
            const lastName = s.last_name || fam?.last_name || '';
            const fullName = `${s.first_name || ''} ${lastName}`.trim();
            return (
              <tr
                key={s.id}
                className={[
                  s.is_active ? '' : styles.rowInactive,
                  selection.has(s.id) ? styles.rowSelected : ''
                ].join(' ')}
              >
                <td className={styles.checkCell}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${fullName}`}
                    checked={selection.has(s.id)}
                    onChange={() => onToggleRow(s.id)}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className={styles.entityBtn}
                    onClick={() => onViewDetail(s)}
                  >
                    <span className={styles.entityName}>{fullName || '—'}</span>
                    <span className={styles.entityMeta}>
                      {fam ? `${fam.first_name || ''} ${fam.last_name || ''}` : ''}
                    </span>
                  </button>
                </td>
                <td>
                  <span className={styles.subtle}>{s.student_id || '—'}</span>
                </td>
                <td>
                  <span className={styles.subtle}>{formatDate(s.dob)}</span>
                </td>
                <td>
                  {fam ? (
                    <span className={styles.familyChip}>
                      <Badge variant="neutral">#{fam.case_number}</Badge>
                    </span>
                  ) : (
                    <span className={styles.subtle}>—</span>
                  )}
                </td>
                <td>
                  <RowLabelPicker
                    entity={s}
                    available={labelsAvailable}
                    onCommit={onCommitRowLabels}
                    onCreateLabel={onCreateLabelRow}
                  />
                </td>
                <td className={styles.alignRight}>
                  <span className={styles.subtle}>{formatMoney(s.expected)}</span>
                </td>
                <td className={styles.alignRight}>
                  <span className={styles.subtle}>{formatMoney(s.received)}</span>
                </td>
                <td className={styles.compactBadgeCell}>
                  <Badge variant={s.is_active ? 'success' : 'neutral'}>
                    {s.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className={styles.actionsCell}>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.linkAction}
                      onClick={() => onEdit(s)}
                    >
                      <FaPen aria-hidden="true" /> Edit
                    </button>
                  <button
                    type="button"
                    className={[styles.linkAction, styles.danger].join(' ')}
                    onClick={() => onDelete(s)}
                    aria-label="Deactivate student"
                  >
                    <FaTrash aria-hidden="true" />
                  </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
